<template>
  <div class="task-log">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column is-full">
            <div class="box">
              <div class="columns is-mobile" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
              </div>
              <div class="columns no-margin" style="margin-top: 16px">
                <div class="column is-full">
                  <b-field required :label="$t('task-log-view.select-date-title')">
                    {{ getFormattedDate(selected_date) }}
                  </b-field>
                  <b-field required :label="$t('task-log-view.task')">
                    <b-taginput
                        v-if="isInNewMode"
                        maxtags="1"
                        v-model="tasksTags"
                        :data="filteredTasks"
                        :loading="false"
                        :has-counter="false"
                        autocomplete
                        :open-on-focus="true"
                        field="title"
                        icon="format-list-checks"
                        :remove-on-keys=[]
                        :placeholder="$t('task-log-view.task-placeholder')"
                        tabindex="2"
                        @typing="getFilteredTasks">
                    </b-taginput>
                    <label v-else>{{ log_data.parent.title }}</label>
                  </b-field>
                </div>
              </div>
              <div class="columns no-margin">
                <div class="column is-half">
                  <b-field required :label="$t('task-log-view.hours-int')">
                    <b-numberinput :editable="true" tabindex="3" v-model="internalHour" min="0" step="1">
                    </b-numberinput>
                  </b-field>
                </div>
                <div class="column is-half">
                  <b-field required :label="$t('task-log-view.minutes-int')">
                    <b-numberinput :editable="false" tabindex="4" v-model="internalMinutes" min="0" max="45"
                                   step="15">
                    </b-numberinput>
                  </b-field>
                </div>
              </div>
              <div class="columns no-margin">
                <div class="column is-half">
                  <b-field required :label="$t('task-log-view.hours-ext')">
                    <b-numberinput :editable="true" tabindex="5" v-model="externalHour" min="0" step="1">
                    </b-numberinput>
                  </b-field>
                </div>
                <div class="column is-half">
                  <b-field required :label="$t('task-log-view.minutes-ext')">
                    <b-numberinput :editable="false" tabindex="6" v-model="externalMinutes" min="0" max="45"
                                   step="15">
                    </b-numberinput>
                  </b-field>
                </div>
              </div>
              <div class="columns no-margin">
                <div class="column is-half">
                  <b-field required :label="quantityTitle">
                    <b-numberinput :editable="true" tabindex="7" v-model="quantity" min-step="0.00001">
                    </b-numberinput>
                  </b-field>
                </div>
                <div class="column is-half">
                  <b-field style="margin-top: 40px;">
                    {{remainingQuantity}}
                  </b-field>
                </div>
              </div>
              <div class="columns no-margin">
                <div class="column is-full">
                  <b-field :label="$t('task-log-view.comments')">
                    <b-input v-model="text"
                             :placeholder="$t('task-log-view.comments-placeholder')" tabindex="8"
                             type="textarea"></b-input>
                  </b-field>
                  <label v-if="!isFormValid"
                         class="required-field">{{ $t('validation.task-log-form') }}
                  </label>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px">
                <div class="column is-narrow">
                  <b-button tabindex="10" class="button" @click="cancelClicked" icon-left="close-thick">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button tabindex="11" class="button is-primary" @click="saveClicked"
                            icon-left="content-save" :disabled="!isEnableSaveButton">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "TaskLog",
  components: {},
  props: {
    'project_id': {
      type: String,
    },
    'log_data': {},
    'tasks_data': {},
    'selected_date': {}
  },
  data() {
    return {
      tasksTags: [],
      filteredTasks: this.tasks_data,
      title: "",
      internalHour: 0,
      internalMinutes: 0,
      externalHour: 0,
      externalMinutes: 0,
      quantity: 0.0,
      text: "",
      isLoading: false,
      isFormValid: true
    }
  },
  methods: {
    createTaskLog: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertTaskLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_log";
      insertTaskLogFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.task-log-created'));
        this.$emit('taskLogAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'))
        console.error("Error writing document:", error?.code);
      });
    },
    updateTaskLog: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updateTaskLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_log";
      updateTaskLogFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.task-log-updated'));
        this.$emit('taskLogUpdated');
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'))
        console.error("Error writing document:", error?.code);
      });
    },
    getFilteredTasks(text) {
      this.filteredTasks = this.tasks_data.filter((option) => {
        return option.title
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    validateForm() {
      return this.selected_date != null && this.quantity != null
            && ((this.isInNewMode && this.tasksTags != null && this.tasksTags.length > 0) || (!this.isInNewMode && this.title != ""))
    },
    /*
     UI Events
     */
    cancelClicked: function () {
      this.$emit('close');
    },
    backClicked: function () {
      this.$router.back();
    },
    saveClicked: function () {
      this.isFormValid = true
      if (!this.validateForm()) {
        this.isFormValid = false
        return
      }
      // setting current time to the selected date
      const currentDate = new Date()
      this.selected_date.setHours(currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds(), currentDate.getMilliseconds())
      const dataFieldsToUpdate = {
        project_id: this.project_id,
        duration_internal: (this.internalHour + (this.internalMinutes / 60)),
        duration_external: (this.externalHour + (this.externalMinutes / 60)),
        quantity: this.quantity,
        text: this.text
      }
      if (this.isInNewMode){
        dataFieldsToUpdate.date = this.selected_date.toISOString();
        dataFieldsToUpdate.task_id = this.tasksTags[0].task.document_id;
        this.createTaskLog(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.task_id = this.log_data.task_id;
        dataFieldsToUpdate.document_id = this.log_data.document_id;
        this.updateTaskLog(dataFieldsToUpdate);
      }
    }
  },
  mounted() {
    if (this.isInNewMode && this.currentUserAccess("ProjectLogs", this.UserActions.CREATE, this.project_id)) {
      console.log("add new task log");
    } else if (!this.isInNewMode && this.currentUserAccess("ProjectLogs", this.UserActions.UPDATE, this.project_id)){
      console.log("update task log");
      this.title = this.log_data.parent.title;
      this.internalHour  = Math.floor(this.log_data.duration_internal)
      this.internalMinutes = (this.log_data.duration_internal % 1).toFixed(2) * 60;
      this.externalHour  = Math.floor(this.log_data.duration_external)
      this.externalMinutes = (this.log_data.duration_external % 1).toFixed(2) * 60;
      this.quantity = this.log_data.quantity;
      this.text = this.log_data.text; 
    } else {
      console.log("component access denied.");
      this.$emit('close');
    }
  },
  computed: {
    isInNewMode() {
      return this.log_data.document_id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('task-log-view.new-log-title')
      } else {
        return this.$t('task-log-view.edit-log-title')
      }
    },
    quantityTitle() {
      let unit, unitKey;
      if (this.isInNewMode) {
        if (this.tasksTags[0] === undefined || this.tasksTags[0] == null
          || this.tasksTags[0].task.meta.estimation === undefined || this.tasksTags[0].task.meta.estimation == null){
          return this.$t('task-log-view.quantity');
        }
        else
          unit = this.tasksTags[0].task.unit;
      }
      else {
        unit =this.log_data.parent.unit;
      }
      unitKey = this.quantityUnits.find(obj => {
            return obj.id === unit
        });
      return this.$t('task-log-view.quantity') + ((unitKey === undefined || unitKey === "") ? "" : " (" + this.$t(unitKey.translationKey) + ")");
    },
    isEnableSaveButton() {
      return (this.internalHour + this.internalMinutes + this.externalHour + this.externalMinutes) > 0;
    },
    remainingQuantity(){
      let tag;
      let totalQuantity = 0;
      let totalLoggedQuantity = 0;
      let curLogSavedQuantity = 0;
      if (this.isInNewMode) {
        if(this.tasksTags[0] === undefined || this.tasksTags[0] == null)
           return '';
        tag = this.tasksTags[0].task;
        if(tag.meta.estimation === undefined || tag.meta.estimation == null)
          return;
        totalLoggedQuantity = tag.logged_quantities;
      }
      else {
        tag = this.log_data;
        totalLoggedQuantity = tag.parent.logged_quantities;
        curLogSavedQuantity = tag.quantity;
      }
      totalQuantity = tag.meta.estimation.quantity;
      let remaining = (totalLoggedQuantity - totalQuantity - curLogSavedQuantity + this.quantity).toFixed(3);
      return ((remaining>0) ? this.$t('project-logs.exceeding') : this.$t('project-logs.remaining')) + ": " + Math.abs(remaining);
    }
  }
};
</script>

<style scoped>
.required-field {
  color: red;
  font-size: 10pt;
}
.no-margin {
  margin-bottom: 0px !important;
}
</style>
